import { useMemo, useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, Plus } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import { LiksIconButton } from '../../../components/liks/LiksIconButton/LiksIconButton';
import { LiksSearch } from '../../../components/liks/LiksSearch/LiksSearch';
import { useCreateLacUserMutation } from '../../../features/lac/mutations/useLacUserMutations';
import { useLacTenantOverviewsQuery } from '../../../features/lac/queries/useLacTenantOverviewsQuery';
import { useLacUsersByTenantQuery } from '../../../features/lac/queries/useLacUsersByTenantQuery';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import type {
  LacCreateUserInput,
  LacTenantUsersGroup,
} from '../../../types/lac';
import { toast } from '../../../toast/toast';
import { CreateLacUserDialog } from '../CreateLacUserDialog';
import { LacUsersTenantGroup } from '../LacUsersTenantGroup';
import { filterGroup, type StatusFilter } from '../lacUsersTabFilters';
import './LacUsersTab.css';

export function LacUsersTab() {
  const { translate } = useI18n();
  const usersByTenantQuery = useLacUsersByTenantQuery();
  const tenantOverviewsQuery = useLacTenantOverviewsQuery();
  const createUserMutation = useCreateLacUserMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [collapsedTenantNames, setCollapsedTenantNames] = useState(
    () => new Set<string>(),
  );

  const groups = useMemo(
    () => usersByTenantQuery.data ?? [],
    [usersByTenantQuery.data],
  );
  const isLoading = usersByTenantQuery.isLoading;
  const isError = usersByTenantQuery.isError;

  const overviewByTenant = useMemo(() => {
    const map = new Map<
      string,
      {
        userCount?: number | null;
        maxUsers?: number | null;
        metricsByUserKey: Map<string, number>;
      }
    >();

    for (const overview of tenantOverviewsQuery.data ?? []) {
      const tenantName = overview.tenantName?.trim();
      if (!tenantName) {
        continue;
      }

      const metricsByUserKey = new Map<string, number>();
      for (const metric of overview.userKnowledgeBaseMetrics ?? []) {
        const count = metric.knowledgeBaseCount ?? 0;
        const userId = metric.userId?.trim();
        const username = metric.username?.trim();
        if (userId) {
          metricsByUserKey.set(userId, count);
        }
        if (username) {
          metricsByUserKey.set(username, count);
        }
      }

      map.set(tenantName, {
        userCount: overview.userCount,
        maxUsers: overview.maxUsers,
        metricsByUserKey,
      });
    }

    return map;
  }, [tenantOverviewsQuery.data]);

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return groups
      .map((group) => filterGroup(group, query, statusFilter))
      .filter((group): group is LacTenantUsersGroup => group !== null);
  }, [groups, searchQuery, statusFilter]);

  const areAllExpanded =
    filteredGroups.length > 0 &&
    filteredGroups.every(
      (group) => !collapsedTenantNames.has(group.tenantName),
    );

  const toggleTenant = (tenantName: string) => {
    setCollapsedTenantNames((prev) => {
      const next = new Set(prev);
      if (next.has(tenantName)) {
        next.delete(tenantName);
      } else {
        next.add(tenantName);
      }
      return next;
    });
  };

  const expandAllTenants = () => {
    setCollapsedTenantNames(new Set());
  };

  const collapseAllTenants = () => {
    setCollapsedTenantNames(
      new Set(filteredGroups.map((group) => group.tenantName)),
    );
  };

  const handleStatusFilterChange = (value: string) => {
    if (value === 'all' || value === 'enabled' || value === 'disabled') {
      setStatusFilter(value);
    }
  };

  const handleCreateUser = (input: LacCreateUserInput) => {
    createUserMutation.mutate(input, {
      onSuccess: (data) => {
        if (data.roleAssigned) {
          toast.success({
            title: translate('lac.users.create.successTitle'),
            description: translate('lac.users.create.successDescription', {
              username: input.username,
            }),
          });
        }
        setIsCreateDialogOpen(false);
        if (!data.roleAssigned) {
          toast.error({
            title: translate('lac.users.create.roleErrorTitle'),
            description: translate('lac.users.create.roleErrorDescription', {
              username: input.username,
            }),
          });
        }
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('lac.users.create.errorTitle'),
          translate('lac.users.create.errorDescription'),
        );
      },
    });
  };

  if (isLoading) {
    return (
      <p className="lac-users__status">{translate('lac.users.loading')}</p>
    );
  }

  if (isError) {
    return (
      <p className="lac-users__status" role="alert">
        {translate('lac.users.loadError')}
      </p>
    );
  }

  return (
    <>
      <div className="lac-users">
        <div className="lac-users__toolbar">
          <LiksSearch
            className="lac-users__search"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={translate('lac.users.searchPlaceholder')}
            aria-label={translate('lac.users.searchLabel')}
            clearLabel={translate('common.search.clear')}
            variant="on-light"
          />
          <LiksDropdown
            className="lac-users__status-filter"
            variant="on-light"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            options={[
              {
                value: 'all',
                label: translate('lac.users.statusFilter.all'),
              },
              {
                value: 'enabled',
                label: translate('lac.users.enabled'),
              },
              {
                value: 'disabled',
                label: translate('lac.users.disabled'),
              },
            ]}
            aria-label={translate('lac.users.statusFilter.label')}
          />
          <LiksButton
            className="lac-users__create-button"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus strokeWidth={2} aria-hidden />
            {translate('lac.users.create.open')}
          </LiksButton>
          <LiksIconButton
            className="lac-users__expand-toggle"
            variant="outlined"
            disabled={filteredGroups.length === 0}
            onClick={areAllExpanded ? collapseAllTenants : expandAllTenants}
            aria-label={
              areAllExpanded
                ? translate('lac.users.collapseAll')
                : translate('lac.users.expandAll')
            }
            title={
              areAllExpanded
                ? translate('lac.users.collapseAll')
                : translate('lac.users.expandAll')
            }
          >
            {areAllExpanded ? (
              <ChevronsDownUp strokeWidth={2} aria-hidden />
            ) : (
              <ChevronsUpDown strokeWidth={2} aria-hidden />
            )}
          </LiksIconButton>
        </div>

        {groups.length === 0 ? (
          <p className="lac-users__empty">{translate('lac.users.empty')}</p>
        ) : filteredGroups.length === 0 ? (
          <p className="lac-users__empty">
            {translate('lac.users.noSearchResults')}
          </p>
        ) : (
          <ul
            className="lac-users__list liks-scrollbar"
            aria-label={translate('lac.tabs.users')}
          >
            {filteredGroups.map((group) => {
              const overview = overviewByTenant.get(group.tenantName);
              return (
                <LacUsersTenantGroup
                  key={group.tenantName}
                  group={group}
                  isExpanded={!collapsedTenantNames.has(group.tenantName)}
                  userCount={overview?.userCount ?? group.totalUserCount}
                  maxUsers={overview?.maxUsers ?? group.maxUsers}
                  metricsByUserKey={overview?.metricsByUserKey ?? new Map()}
                  onToggle={toggleTenant}
                />
              );
            })}
          </ul>
        )}
      </div>

      {isCreateDialogOpen && (
        <CreateLacUserDialog
          onCreate={handleCreateUser}
          onClose={() => setIsCreateDialogOpen(false)}
          isSubmitting={createUserMutation.isPending}
        />
      )}
    </>
  );
}
